import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Clé localStorage de l'id d'appareil (stable par navigateur/origine). */
const DEVICE_ID_KEY = 'nawam.deviceId';
const SW_URL = '/firebase-messaging-sw.js';

/**
 * Enregistrement de l'appareil pour les notifications push **web** (FCM).
 * Complémentaire au temps réel in-app ({@link NotificationSocketService}) :
 * le push couvre l'arrière-plan (onglet fermé / app non focus) via le service
 * worker `public/firebase-messaging-sw.js`.
 *
 * Back : `POST /notification/api/devices/register { token, deviceType:'WEB', deviceId }`.
 *
 * UX : on ne demande JAMAIS la permission automatiquement (les navigateurs
 * pénalisent/bloquent les prompts non sollicités) — {@link enable} est déclenché
 * par un geste utilisateur (toggle dans la cloche).
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/notification/api/devices`;

  /** FCM web push supporté par ce navigateur (résolu async via Firebase). */
  readonly supported = signal(false);
  /** État courant de la permission Notification du navigateur. */
  readonly permission = signal<NotificationPermission>('default');
  /** Un token est enregistré pour cette session. */
  readonly enabled = signal(false);
  /** Opération enable/disable en cours. */
  readonly busy = signal(false);

  private app?: FirebaseApp;
  private messaging?: Messaging;
  private swReg?: ServiceWorkerRegistration;
  /** L'écoute foreground (`onMessage`) n'est câblée qu'une fois. */
  private foregroundWired = false;

  /** À appeler une fois au boot authentifié (depuis la cloche). */
  async init(): Promise<void> {
    if (!this.isConfigured() || typeof window === 'undefined' || !('Notification' in window)) {
      this.supported.set(false);
      return;
    }
    this.permission.set(Notification.permission);
    try {
      this.supported.set(await isSupported());
    } catch {
      this.supported.set(false);
      return;
    }
    // Si la permission est déjà accordée (session précédente), on (re)câble
    // l'écoute foreground + on rafraîchit le token côté serveur — sinon les
    // notifs reçues onglet ouvert ne s'afficheraient pas après un simple reload.
    if (this.supported() && this.permission() === 'granted') {
      await this.registerAndListen();
    }
  }

  /** Demande la permission, récupère le token FCM et enregistre l'appareil. */
  async enable(): Promise<boolean> {
    if (!this.supported() || this.busy()) return false;
    this.busy.set(true);
    try {
      const perm = await Notification.requestPermission();
      this.permission.set(perm);
      if (perm !== 'granted') return false;
      return await this.registerAndListen();
    } catch (err) {
      console.error('[push] enable failed', err);
      return false;
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Enregistre le SW + le token FCM côté serveur et câble l'écoute foreground.
   * Idempotent (réappelable au boot pour rafraîchir le token).
   */
  private async registerAndListen(): Promise<boolean> {
    const messaging = this.ensureMessaging();
    if (!messaging) return false;

    this.swReg = await navigator.serviceWorker.register(SW_URL);
    const token = await getToken(messaging, {
      vapidKey: environment.firebaseVapidKey,
      serviceWorkerRegistration: this.swReg,
    });
    if (!token) return false;

    await firstValueFrom(
      this.http.post(`${this.base}/register`, {
        token,
        deviceType: 'WEB',
        deviceId: this.deviceId(),
      }),
    );

    this.listenForeground(messaging);
    this.enabled.set(true);
    return true;
  }

  /**
   * Affiche les messages reçus pendant que l'onglet est au **premier plan**.
   * FCM web n'affiche JAMAIS automatiquement dans ce cas (contrairement à
   * l'arrière-plan géré par le service worker) — il faut le faire à la main.
   * On passe par `registration.showNotification` (plus fiable que
   * `new Notification()`, bloqué sur Chrome Android).
   */
  private listenForeground(messaging: Messaging): void {
    if (this.foregroundWired) return;
    this.foregroundWired = true;
    onMessage(messaging, (payload) => {
      const data = (payload.data ?? {}) as Record<string, string>;
      const title = payload.notification?.title || data['title'] || 'Nawam';
      const body =
        payload.notification?.body || data['messageFR'] || data['message'] || '';
      const reg = this.swReg;
      if (reg) {
        void reg.showNotification(title, {
          body,
          icon: '/images/logo/nawam-logo.png',
          badge: '/images/logo/nawam-logo.png',
          data,
        });
      }
    });
  }

  /** Révoque le token local + dé-enregistre l'appareil côté serveur. */
  async disable(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    try {
      const messaging = this.ensureMessaging();
      if (messaging) {
        try { await deleteToken(messaging); } catch { /* déjà invalide */ }
      }
      await firstValueFrom(
        this.http.delete(`${this.base}/unregister`, {
          params: new HttpParams().set('deviceId', this.deviceId()),
        }),
      ).catch(() => { /* best-effort */ });
      this.enabled.set(false);
    } finally {
      this.busy.set(false);
    }
  }

  // ── Internes ────────────────────────────────────────────────────────────

  private ensureMessaging(): Messaging | undefined {
    if (this.messaging) return this.messaging;
    if (!this.isConfigured()) return undefined;
    this.app ??= initializeApp(environment.firebase);
    this.messaging = getMessaging(this.app);
    return this.messaging;
  }

  /** Config Firebase réellement renseignée (placeholders non remplacés → off). */
  private isConfigured(): boolean {
    const f = environment.firebase;
    return (
      !!f &&
      !!f.apiKey &&
      !f.apiKey.startsWith('REPLACE_') &&
      !!environment.firebaseVapidKey &&
      !environment.firebaseVapidKey.startsWith('REPLACE_')
    );
  }

  private deviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }
}
